/**
 * Speakeasy site-wide namespace + accessibility control bar logic.
 * Loaded deferred from layout/theme.liquid.
 *
 * Public API (see scratchpad CONTRACT.md):
 *   window.Speakeasy.toast(message)
 *     Shows the shared .spk-toast pill ("✦ " + message), role="status",
 *     auto-dismisses after 2.4s, re-triggerable.
 *   document CustomEvent 'speakeasy:toast' with detail.message -> same toast.
 *
 * State contract (restored pre-paint by snippets/speakeasy-tokens.liquid):
 *   html[data-access-mode='on']  <-> localStorage 'viking:accessMode' ('on'/'off')
 *   html[data-text-step='1'|'2'] <-> localStorage 'viking:textStep' ('0'|'1'|'2')
 * This script only syncs the a11y-bar UI from those attributes and writes
 * changes back; it never re-reads storage to decide state.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var MODE_KEY = 'viking:accessMode';
  var STEP_KEY = 'viking:textStep';
  var TOAST_DURATION = 2400;

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      /* Storage unavailable (private mode, blocked cookies) — state still
         applies for this page via the html attributes. */
    }
  }

  /* ---------------------------------------------------------------- toast */

  var toastElement = null;
  var toastTimer = null;

  function toast(message) {
    if (!message || !document.body) return;

    if (!toastElement || !toastElement.isConnected) {
      toastElement = document.createElement('div');
      toastElement.className = 'spk-toast';
      toastElement.setAttribute('role', 'status');
      toastElement.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastElement);
    }

    window.clearTimeout(toastTimer);
    toastElement.hidden = false;

    /* Restart the pop-in animation when re-triggered mid-display. */
    toastElement.style.animation = 'none';
    void toastElement.offsetWidth;
    toastElement.style.animation = '';

    toastElement.textContent = '✦ ' + String(message);

    toastTimer = window.setTimeout(function () {
      toastElement.hidden = true;
      toastElement.textContent = '';
    }, TOAST_DURATION);
  }

  window.Speakeasy = window.Speakeasy || {};
  window.Speakeasy.toast = toast;

  document.addEventListener('speakeasy:toast', function (event) {
    if (event && event.detail && event.detail.message) {
      toast(event.detail.message);
    }
  });

  /* ------------------------------------------- accessibility control bar */

  function accessModeOn() {
    return root.getAttribute('data-access-mode') === 'on';
  }

  function currentStep() {
    var step = root.getAttribute('data-text-step');
    return step === '1' || step === '2' ? step : '0';
  }

  function stepButtons() {
    return document.querySelectorAll('.spk-a11y-step');
  }

  /* Sync the bar's ARIA state from the html attributes (visual state is
     CSS-driven off the same attributes, so it is already correct). */
  function syncBarUi() {
    var toggle = document.getElementById('spk-a11y-toggle');
    if (toggle) {
      toggle.setAttribute('aria-checked', accessModeOn() ? 'true' : 'false');
    }

    var step = currentStep();
    var buttons = stepButtons();
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].setAttribute(
        'aria-pressed',
        buttons[i].getAttribute('data-step') === step ? 'true' : 'false'
      );
    }
  }

  function setAccessMode(enabled) {
    if (enabled) {
      root.setAttribute('data-access-mode', 'on');
    } else {
      root.removeAttribute('data-access-mode');
    }
    writeStorage(MODE_KEY, enabled ? 'on' : 'off');
    syncBarUi();
  }

  function setTextStep(step) {
    if (step === '1' || step === '2') {
      root.setAttribute('data-text-step', step);
    } else {
      step = '0';
      root.removeAttribute('data-text-step');
    }
    writeStorage(STEP_KEY, step);
    syncBarUi();
  }

  function initBar() {
    var toggle = document.getElementById('spk-a11y-toggle');
    if (toggle) {
      /* <button> fires click on Enter and Space natively. */
      toggle.addEventListener('click', function () {
        setAccessMode(!accessModeOn());
      });
    }

    var buttons = stepButtons();
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', function (event) {
        setTextStep(event.currentTarget.getAttribute('data-step'));
      });
    }

    syncBarUi();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBar);
  } else {
    initBar();
  }
})();
